import type React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  CardMedia,
  Divider,
  Stack,
  Chip,
} from "@mui/material";

interface VenueDetailsDialogProps {
  open: boolean;
  venue: any;
  onClose: () => void;
  onHover?: () => void;
  onLeave?: () => void;
}

const VenueDetailsDialog: React.FC<VenueDetailsDialogProps> = ({
  open,
  venue,
  onClose,
  onHover,
  onLeave,
}) => {
  const realImages = venue?.files?.map((f: any) => f.file) || [];

  const headerImage =
    venue?.files?.find((f: any) => f.is_cover)?.file || realImages[0] || null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      PaperProps={{
        sx: { borderRadius: 3, overflow: "hidden" },
      }}
    >
      {/* Header Image */}
      {/* {headerImage && (
        <CardMedia
          component="img"
          height="230"
          // image={headerImage}
          alt="Venue Header"
          sx={{ objectFit: "cover" }}
        />
      )} */}

      <DialogContent sx={{ mt: 2 }}>
        {realImages.length > 1 && (
          <Grid container spacing={1} mb={3}>
            {realImages.slice(0, 4).map((img: string, index: number) => (
              <Grid size={{ xs: 3 }} key={index}>
                <CardMedia
                  component="img"
                  image={img}
                  alt={`Venue Image ${index}`}
                  sx={{
                    width: "100%",
                    height: 120,
                    objectFit: "cover",
                    borderRadius: 2,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Venue Info */}
        <Typography variant="h6" mb={2} fontWeight="bold">
          Venue Details
        </Typography>

        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>Name:</strong> {venue?.name || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Type:</strong> {venue?.venue_type || "N/A"}
            </Typography>
            <Typography variant="body2" gutterBottom>
              <strong>Address:</strong>{" "}
              {venue
                ? `${venue.address_one || ""}, ${venue.address_two || ""} ${
                    venue.suburb || ""
                  }, ${venue.city || ""}`
                : "N/A"}
            </Typography>
          </Box>

          <Divider />

          {/* Technical Info */}
          <Box>
            <Typography variant="subtitle2" fontWeight="bold" mb={1}>
              Technical Details
            </Typography>
            <Stack direction="row" gap={1} flexWrap="wrap">
              {venue?.has_power && (
                <Chip
                  label={`Power: ${venue?.power_type || "N/A"}`}
                  size="small"
                />
              )}
              {venue?.power_backup && (
                <Chip label={`Backup: ${venue.power_backup}`} size="small" />
              )}
              {venue?.has_pa_system && (
                <Chip label="PA System Available" size="small" />
              )}
              {venue?.backup_power_capacity && (
                <Chip
                  label={`Capacity: ${venue.backup_power_capacity}`}
                  size="small"
                />
              )}
            </Stack>
          </Box>

          {/* Connectivity Info */}
          {(venue?.internet_upload_speed ||
            venue?.internet_service_provider) && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  Internet Details
                </Typography>
                {venue?.internet_service_provider && (
                  <Typography variant="body2" gutterBottom>
                    <strong>ISP:</strong> {venue.internet_service_provider}
                  </Typography>
                )}
                {venue?.internet_upload_speed && (
                  <Typography variant="body2">
                    <strong>Upload Speed:</strong> {venue.internet_upload_speed}{" "}
                    Mbps
                  </Typography>
                )}
              </Box>
            </>
          )}

          {/* Layout capacities */}
          {venue?.layouts?.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  Room Layouts
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {venue.layouts.map((layout: any, i: number) => (
                    <Chip
                      key={i}
                      label={`Capacity: ${layout.capacity}`}
                      size="small"
                    />
                  ))}
                </Stack>
              </Box>
            </>
          )}

          {/* Notes */}
          {venue?.notes && (
            <>
              <Divider />
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" mb={1}>
                  Additional Notes
                </Typography>
                <Typography variant="body2">{venue.notes}</Typography>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => window.open(`/venues/view/${venue?.id}`, "_blank")}
        >
          Open Full Venue
        </Button>

        <Button variant="outlined" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VenueDetailsDialog;
